import { getConfig } from './config';
import { clearStorage, getStoredToken } from './storage';
import { isTokenExpired } from './token';
import {
  type ApiErrorResponse,
  type ApiSuccessResponse,
  FirebirdError
} from './types';

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  useAuth = false
): Promise<T> {
  const config = getConfig();
  const url = `${config.baseUrl}/api/v1${path}`;

  const headers: Record<string, string> = {
    'X-API-Key': config.apiKey,
    'Content-Type': 'application/json'
  };

  if (useAuth) {
    const token = getStoredToken();
    if (!token) {
      throw new FirebirdError(
        'UNAUTHORIZED',
        'Not authenticated. Call connectSpotify() first.'
      );
    }
    // Drop a session that has already lapsed rather than spending a round
    // trip to be told so, and so the next call re-runs OAuth.
    if (isTokenExpired(token)) {
      clearStorage();
      throw new FirebirdError(
        'UNAUTHORIZED',
        'Session expired. Call connectSpotify() again.'
      );
    }
    headers.Authorization = `Bearer ${token}`;
  }

  const resp = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined
  });

  let json: unknown;
  try {
    json = await resp.json();
  } catch {
    throw new FirebirdError(
      'NETWORK_ERROR',
      `Request failed: server returned non-JSON response (${resp.status})`
    );
  }

  if (!resp.ok) {
    // The server rejected our session — it is dead whatever the reason.
    // Clear it so the next call re-runs OAuth instead of replaying the
    // same failure on every reload. A 401 without useAuth is an API-key
    // problem and says nothing about the fan's session, so leave it be.
    if (resp.status === 401 && useAuth) {
      clearStorage();
    }

    const errBody = json as ApiErrorResponse;
    throw new FirebirdError(
      errBody.error?.code || 'UNKNOWN_ERROR',
      errBody.error?.message || `Request failed: ${resp.status}`,
      errBody.error?.details
    );
  }

  return (json as ApiSuccessResponse<T>).data;
}

export function get<T>(path: string, useAuth = false): Promise<T> {
  return request<T>('GET', path, undefined, useAuth);
}

export function post<T>(
  path: string,
  body: unknown,
  useAuth = false
): Promise<T> {
  return request<T>('POST', path, body, useAuth);
}
